import React, { useContext, useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { I18nContext } from '../../context/translations/TranslationsContext.context';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { PiggyBank, Calculator, Lightbulb, Settings2, Sparkles } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { fmtEur, fmtNum } from '../../utils/formatArcaNumbers';
import ConfigurarRentaMensualDialogV2, {
	RentaMensualConfig,
	MIN_MONTHLY_RENTA,
	RENTA_MENSUAL_DAY
} from './ConfigurarRentaMensualDialogV2';
import { AnnuityState } from '../../api/pension-plan-withdrawals/interfaces/WithdrawalRequest';

/**
 * ⚠️ UX SCAFFOLD (tarea Notion "Definimos MVP rescates") — mismo patrón visual
 * que `ProjectionSimulatorV2` (panel a la izquierda, educación a la derecha),
 * pero para RENTA MENSUAL en vez de aportación. El slider de importe siempre
 * está visible (arranca en 0 si aún no hay nada configurado — inactiva de
 * verdad, sin un importe "óptimo" inventado — y sirve de preview antes de
 * guardar); en cuanto hay una config guardada, el slider se bloquea para
 * reflejarla y ya no se puede volver a mover "desde fuera" — cualquier cambio
 * real (importe final, pausa) se hace desde el modal
 * ConfigurarRentaMensualDialogV2 (el día de ingreso lo fija Caser, siempre el
 * 25, no es configurable). Pausada o inactiva se pintan igual, en un
 * gris más claro que el texto secundario habitual (ver `.slider-arca-muted` /
 * `.text-muted-light` en index.css) — solo se ve en verde una renta realmente
 * activa. No persiste nada real: no existe hoy ningún endpoint ni operación
 * SOAP de escritura de rescate (ver Notion). NO SEGUIR ESTE ARCHIVO COMO
 * REFERENCIA DE CALIDAD — sin tests, sin conexión a datos reales.
 */

export interface RescueRentaSimulatorV2Props {
	availableToRescue: number;
	// Email del usuario que confirma — se pasa tal cual al modal de
	// configuración.
	email: string;
	// Motivo ya establecido, si lo hay (ver rescueDocsCatalog.ts) — se pasa tal
	// cual al modal de configuración, que lo bloquea si es Jubilación.
	currentReason?: string | null;
	// Se pasa tal cual al modal de configuración: importe rescatable SOLO por
	// antigüedad (tramo +10 años), para recalcular el máximo si el motivo
	// elegido dentro del modal cambia a "Antigüedad".
	seniorityAmount?: number;
	// Plan y token: viajan al modal, que es quien registra la solicitud.
	userEppConfigId?: string | null;
	token?: string | null;
	// Renta mensual YA aprobada por el equipo, derivada de las solicitudes
	// confirmadas (ver `findAnnuityState`). Es lo único que sabe de verdad si la
	// renta está activa: sin esto la card volvía a "inactiva" al recargar.
	annuityState?: AnnuityState | null;
	// Hay una solicitud de renta viva (PENDING) para este plan. Bloquea mandar
	// otra —el backend sólo admite una— y evita que la card dé por hecha una
	// gestión que todavía no ha tramitado nadie.
	pendingAnnuity?: boolean;
	onSubmitted?: () => void;
	onConfigureRenta?: (monthlyAmount: number) => void;
	onOpenCalculator?: () => void;
}

// Ejemplo ilustrativo (no ligado al importe real del usuario): de cada 100€
// rescatados, cuánto se queda Hacienda según la forma de cobro elegida.
const EXAMPLE_CHART_DATA = [
	{ nameKey: 'rescue.rentaSimulator.chartLabelLumpSum', neto: 59, impuestos: 41 },
	{ nameKey: 'rescue.rentaSimulator.chartLabelGradual', neto: 78, impuestos: 22 }
];

const EDUCATION_TIPS = [
	'rescue.rentaSimulator.tip1',
	'rescue.rentaSimulator.tip2',
	'rescue.rentaSimulator.tip3'
];

const RescueRentaSimulatorV2: React.FC<RescueRentaSimulatorV2Props> = ({
	availableToRescue,
	email,
	currentReason,
	seniorityAmount,
	userEppConfigId,
	token,
	annuityState,
	pendingAnnuity,
	onSubmitted,
	onConfigureRenta,
	onOpenCalculator
}) => {
	const {
		state: { translate }
	} = useContext(I18nContext);
	const [showConfigModal, setShowConfigModal] = useState(false);

	// La renta APROBADA es lo único que se pinta como real. Lo que se acaba de
	// mandar no cuenta: es una solicitud que tramita una persona a mano, y
	// darla por hecha ya nos costó pintar "pausada" a quien seguía cobrando. El
	// día lo fija Caser (el 25), nunca se elige.
	const config: RentaMensualConfig | null = annuityState
		? { amount: annuityState.monthlyAmount, day: RENTA_MENSUAL_DAY, paused: annuityState.paused }
		: null;

	// Slider siempre visible: sin renta, arranca en 0 (inactiva de verdad, sin
	// un importe "óptimo" inventado); con renta, refleja el importe vigente.
	const sliderMax = Math.max(Math.ceil(availableToRescue / 12 / 100) * 100, MIN_MONTHLY_RENTA * 2);
	const [monthlyRenta, setMonthlyRenta] = useState(config?.amount ?? 0);

	// Cuando cambia el importe vigente (llega el del backend, o se acaba de
	// guardar otro), el slider lo refleja en vez del preview que hubiera antes.
	// Se depende del número y no del objeto: `config` se recalcula en cada
	// render y la identidad cambiaría siempre.
	const configuredAmount = config?.amount ?? null;
	useEffect(() => {
		if (configuredAmount !== null) setMonthlyRenta(configuredAmount);
	}, [configuredAmount]);

	const monthsLasting = monthlyRenta > 0 ? availableToRescue / monthlyRenta : 0;
	const yearsLasting = monthsLasting / 12;

	// Gris tanto si está pausada como si todavía está inactiva (nunca
	// configurada) — solo se pinta en verde cuando hay una renta realmente activa.
	const isMuted = !config || config.paused;

	// El pendiente manda sobre el estado: mientras el equipo no lo tramite, lo
	// honesto es decir que está pendiente, no adelantar el resultado. El importe
	// de debajo sigue siendo el aprobado, que es el que se está cobrando.
	const statusLabel = pendingAnnuity
		? {
				text: translate('rescue.rentaSimulator.statusPending'),
				className: 'bg-muted text-muted-foreground'
			}
		: !config
			? {
					text: translate('rescue.rentaSimulator.statusInactive'),
					className: 'bg-muted text-muted-foreground'
				}
			: config.paused
				? {
						text: translate('rescue.rentaSimulator.statusPaused'),
						className: 'bg-[#ffeff2] text-[#d4112b]'
					}
				: {
						text: translate('rescue.rentaSimulator.statusActive'),
						className: 'bg-primary/10 text-primary'
					};

	// No se guarda nada en local: la card espera a que el equipo lo apruebe y a
	// que `onSubmitted` recargue el estado real desde el backend.
	const handleSave = (newConfig: RentaMensualConfig) => {
		onConfigureRenta?.(newConfig.amount);
	};

	return (
		<div className="individual-v2">
			<Card className="border-2 border-primary/20 shadow-xs overflow-hidden">
				<CardContent className="p-0">
					<div className="grid grid-cols-1 lg:grid-cols-2">
						{/* Left: renta status + configure */}
						<div className="p-6 lg:border-r border-border min-w-0 flex flex-col">
							<div className="flex-1">
								<div className="flex items-center justify-between gap-2 mb-1">
									<div className="flex items-center gap-2">
										<PiggyBank className="w-5 h-5 text-primary" />
										<h2 className="text-lg font-semibold font-heading">
											{translate('rescue.rentaSimulator.title')}
										</h2>
									</div>
									<span
										className={`text-xs font-semibold px-2.5 py-1 rounded-full shrink-0 ${statusLabel.className}`}
									>
										{statusLabel.text}
									</span>
								</div>
								<p className="text-xs text-muted-foreground mb-4">
									{translate('rescue.rentaSimulator.availablePrefix')}{' '}
									<span className="font-semibold text-foreground">{fmtEur(availableToRescue)}</span>
								</p>

								<div className="mb-4">
									<div className="flex items-baseline justify-between mb-3">
										<span
											className={`text-4xl font-bold font-heading tracking-tight ${
												isMuted ? 'text-muted-light' : 'text-primary'
											}`}
										>
											{fmtNum(monthlyRenta, 0)}€
										</span>
										<span className="text-sm text-muted-foreground">
											{translate('rescue.rentaSimulator.perMonth')}
										</span>
									</div>
									<Slider
										value={[monthlyRenta]}
										onValueChange={(v) => {
											if (Number.isFinite(v[0])) setMonthlyRenta(v[0]);
										}}
										min={MIN_MONTHLY_RENTA}
										max={sliderMax}
										step={10}
										disabled={!!config}
										className={`slider-arca **:[[role=slider]]:bg-primary ${
											isMuted ? 'slider-arca-muted' : ''
										}`}
									/>
									<div className="flex justify-between text-xs text-muted-foreground mt-2">
										<span>
											{translate('rescue.rentaSimulator.minPrefix')} {fmtNum(MIN_MONTHLY_RENTA, 0)}€
										</span>
										<span>{fmtNum(sliderMax, 0)}€</span>
									</div>

									{config ? (
										<p className="text-sm text-muted-foreground mt-3">
											{translate('rescue.rentaSimulator.dayPrefix').replace(
												'{{day}}',
												String(config.day)
											)}
											{!config.paused && (
												<>
													{' '}
													{translate('rescue.rentaSimulator.lastingSuffix')}{' '}
													<span className="font-semibold text-foreground">
														{yearsLasting >= 1
															? `${fmtNum(yearsLasting, 0)} ${translate('rescue.rentaSimulator.years')}`
															: `${fmtNum(monthsLasting, 0)} ${translate('rescue.rentaSimulator.months')}`}
													</span>
												</>
											)}
											.
										</p>
									) : monthlyRenta > 0 ? (
										<p className="text-sm text-muted-foreground mt-3">
											{translate('rescue.rentaSimulator.notConfiguredWithPreview')}{' '}
											<span className="font-semibold text-foreground">
												{yearsLasting >= 1
													? `${fmtNum(yearsLasting, 0)} ${translate('rescue.rentaSimulator.years')}`
													: `${fmtNum(monthsLasting, 0)} ${translate('rescue.rentaSimulator.months')}`}
											</span>
											.
										</p>
									) : (
										<p className="text-sm text-muted-foreground mt-3">
											{translate('rescue.rentaSimulator.notConfiguredNoPreview')}
										</p>
									)}
								</div>

								{pendingAnnuity && (
									<p className="text-sm text-muted-foreground mt-3">
										{translate('rescue.rentaSimulator.pendingNotice')}
									</p>
								)}
							</div>

							<Button
								onClick={() => setShowConfigModal(true)}
								disabled={pendingAnnuity}
								className="w-full gap-2 mt-6 bg-primary hover:bg-primary/90 text-white"
							>
								{config?.paused ? (
									<Sparkles className="w-4 h-4" />
								) : (
									<Settings2 className="w-4 h-4" />
								)}
								{config?.paused
									? translate('rescue.rentaSimulator.reactivateButton')
									: translate('rescue.rentaSimulator.configureButton')}
							</Button>
						</div>

						{/* Right: education */}
						<div className="p-6 bg-primary/5 min-w-0 flex flex-col">
							<div className="flex-1">
								<div className="flex items-center gap-2 mb-3">
									<Lightbulb className="w-5 h-5 text-primary" />
									<h2 className="text-lg font-semibold font-heading">
										{translate('rescue.rentaSimulator.educationTitle')}
									</h2>
								</div>

								<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
									{/* Left: un donut por modalidad, con su nombre encima y su propia
								    leyenda de colores debajo — se entiende cada uno sin tener que
								    comparar anillos ni buscar una leyenda compartida. */}
									<div className="bg-card rounded-lg p-3">
										<div className="grid grid-cols-2 gap-2">
											{EXAMPLE_CHART_DATA.map((row) => (
												<div key={row.nameKey} className="flex flex-col items-center">
													<p className="text-xs font-semibold text-foreground text-center leading-tight mb-1.5">
														{translate(row.nameKey)}
													</p>
													<div className="relative w-20 h-20">
														<ResponsiveContainer width="100%" height="100%">
															<PieChart>
																<Pie
																	data={[
																		{ key: 'neto', value: row.neto },
																		{ key: 'impuestos', value: row.impuestos }
																	]}
																	dataKey="value"
																	innerRadius="68%"
																	outerRadius="100%"
																	startAngle={90}
																	endAngle={-270}
																	stroke="none"
																>
																	{/* arca-green-500 (#0ea44c) = tu dinero (marca);
																    gris claro neutro = lo que pierdes en IRPF. */}
																	<Cell fill="#0ea44c" />
																	<Cell fill="#E5E7EB" />
																</Pie>
															</PieChart>
														</ResponsiveContainer>
														<div className="absolute inset-0 flex items-center justify-center">
															<span className="text-lg font-bold font-heading text-[#0ea44c]">
																{row.neto}%
															</span>
														</div>
													</div>
													<div className="mt-1.5 space-y-0.5 text-[11px] leading-tight">
														<p className="flex items-center gap-1 text-foreground">
															<span className="w-2 h-2 rounded-full bg-[#0ea44c] inline-block shrink-0" />
															{translate('rescue.rentaSimulator.legendYours')} {row.neto}€
														</p>
														<p className="flex items-center gap-1 text-muted-foreground">
															<span className="w-2 h-2 rounded-full bg-[#E5E7EB] border border-gray-300 inline-block shrink-0" />
															{translate('rescue.rentaSimulator.legendTax')} {row.impuestos}€
														</p>
													</div>
												</div>
											))}
										</div>
										<p className="text-[11px] text-muted-foreground mt-2 text-center">
											{translate('rescue.rentaSimulator.chartCaption1')}
										</p>
										<p className="text-[11px] text-muted-foreground text-center">
											{translate('rescue.rentaSimulator.chartCaption2')}
										</p>
									</div>

									{/* Right: numbered tips */}
									<ul className="space-y-2.5">
										{EDUCATION_TIPS.map((tipKey, i) => (
											<li key={i} className="flex items-start gap-2 text-sm text-foreground/80">
												<span className="text-primary font-semibold shrink-0">{i + 1}.</span>
												<span>{translate(tipKey)}</span>
											</li>
										))}
									</ul>
								</div>
							</div>

							<Button
								variant="outline"
								onClick={onOpenCalculator}
								className="w-full border-primary text-primary hover:bg-primary/5 gap-2 mt-6"
							>
								<Calculator className="w-4 h-4" />
								{translate('rescue.rentaSimulator.calcButton')}
							</Button>
						</div>
					</div>
				</CardContent>
			</Card>
			<ConfigurarRentaMensualDialogV2
				userEppConfigId={userEppConfigId}
				token={token}
				onSubmitted={onSubmitted}
				isOpen={showConfigModal}
				onClose={() => setShowConfigModal(false)}
				availableToRescue={availableToRescue}
				email={email}
				currentConfig={config}
				initialAmountOverride={monthlyRenta || undefined}
				currentReason={currentReason}
				seniorityAmount={seniorityAmount}
				onSave={handleSave}
			/>
		</div>
	);
};

export default RescueRentaSimulatorV2;
